import { type FC, type FormEvent, useRef, useState } from "react";
import {
  AppBar,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useCreateContentFromUrlMutation } from "@/partner/entities/contents";
import { useIsMobile } from "@/shared/hooks";
import { useVideoProviderTextKeys } from "../model";
import { isVideoFile } from "./isVideoFile";

interface CreateVideoDialogProps {
  open: boolean;
  onClose: () => void;
  /** Выбранные файлы уходят в общий пайплайн useMultipleUploads (как и drag&drop на UploadsZone) */
  onSelectFiles: (files: File[]) => void;
}

/**
 * Клон референсного AddVideoDialog (Rightholder/Videos/AddVideoDialog): выбор
 * файла и, второй половиной, импорт по прямой ссылке. YouTube и прочие
 * видеохостинги референс тянул отдельным каналом импорта — у нас только
 * прямые ссылки на файл, остальное бэкенд отклоняет с внятной ошибкой.
 */
export const CreateVideoDialog: FC<CreateVideoDialogProps> = ({
  open,
  onClose,
  onSelectFiles,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const providerTextKeys = useVideoProviderTextKeys();
  const isMobile = useIsMobile();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [url, setUrl] = useState("");

  const [createFromUrl, { loading }] = useCreateContentFromUrlMutation({
    // Черновик появляется в списке сразу, ещё без видео — статус доводит
    // существующий поллер processingStatus, как и у загруженного файла
    refetchQueries: ["ContentsList"],
    onCompleted: () => {
      enqueueSnackbar(t(providerTextKeys.createUrlSuccess), {
        variant: "success",
      });
      setUrl("");
      onClose();
    },
    onError: (error) => {
      enqueueSnackbar(error.message, { variant: "error" });
    },
  });

  const handleSubmitUrl = (event: FormEvent) => {
    event.preventDefault();
    const value = url.trim();
    if (!value || loading) return;
    createFromUrl({ variables: { input: { url: value } } });
  };

  const handleFiles = (files: FileList | null) => {
    if (!files || !files.length) return;
    // Тот же фильтр, что и в UploadsZone (drag&drop) — чтобы оба входа
    // принимали одинаковый набор файлов (в т.ч. .mkv с некорректным MIME).
    const videos = Array.from(files).filter(isVideoFile);
    if (!videos.length) return;
    onSelectFiles(videos);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      fullScreen={isMobile}
    >
      <AppBar color="default" position="sticky" elevation={0}>
        <Toolbar>
          <IconButton
            edge="start"
            onClick={onClose}
            size="large"
            sx={{ mr: 1 }}
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" noWrap sx={{ flex: 1 }}>
            {t("videos.create.title")}
          </Typography>
        </Toolbar>
        <Divider />
      </AppBar>

      <DialogContent sx={{ p: 1 }}>
        <Card elevation={0}>
          <CardContent>
            <Stack spacing={2}>
              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={() => fileInputRef.current?.click()}
              >
                {t("videos.create.selectFileButton")}
              </Button>
              <input
                ref={fileInputRef}
                accept="video/*,.mkv"
                type="file"
                multiple
                hidden
                onChange={(event) => handleFiles(event.target.files)}
              />

              <Divider>{t("videos.create.orDivider")}</Divider>

              {/* Вторая половина референсного AddVideoDialog: контент
                  создаётся сразу, файл Cloudflare забирает сам по прямой
                  ссылке, готовность доводят вебхук и поллер — как у
                  загрузки из браузера. */}
              <form onSubmit={handleSubmitUrl}>
                <Stack spacing={2}>
                  <TextField
                    value={url}
                    onChange={(event) => setUrl(event.target.value)}
                    label={t("videos.create.urlLabel")}
                    helperText={t(providerTextKeys.createUrlHelper)}
                    type="url"
                    fullWidth
                    disabled={loading}
                  />
                  <Button
                    type="submit"
                    variant="outlined"
                    fullWidth
                    size="large"
                    disabled={!url.trim() || loading}
                  >
                    {t("videos.create.urlSubmit")}
                  </Button>
                </Stack>
              </form>
            </Stack>
          </CardContent>
        </Card>
      </DialogContent>
    </Dialog>
  );
};
